import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  getConsumerCsMessages,
  listConsumerCsTickets,
  sendConsumerCsMessage,
  submitConsumerCsCsat,
  type ConsumerCsMessage,
} from '@/lib/api';
import { useTk } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';
import { BottomNav } from '@/components/BottomNav';
import { Button } from '@sabay/ui-kit';

export function ConsumerCsConversationPage(): React.ReactElement {
  const tk = useTk();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [search] = useSearchParams();
  const ticketId = search.get('ticketId');

  const auth = useAuth();
  const [messages, setMessages] = useState<ConsumerCsMessage[]>([]);
  const nextSinceRef = useRef(0);
  const bootstrappedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [ticketStatus, setTicketStatus] = useState<string | null>(null);
  const [csatRating, setCsatRating] = useState(5);
  const [csatComment, setCsatComment] = useState('');
  const [csatBusy, setCsatBusy] = useState(false);
  const [csatDone, setCsatDone] = useState(false);

  const refreshTicketStatus = useCallback(async () => {
    if (!auth.token || !ticketId) return;
    try {
      const page = await listConsumerCsTickets(auth.token, 1);
      const t = page.items.find((x) => x.id === ticketId);
      setTicketStatus(t?.status ?? null);
    } catch {
      /* ignore */
    }
  }, [auth.token, ticketId]);

  useEffect(() => {
    void refreshTicketStatus();
  }, [refreshTicketStatus]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || !auth.token || !conversationId) {
      setLoading(false);
      return;
    }

    bootstrappedRef.current = false;
    nextSinceRef.current = 0;
    setMessages([]);
    setLoading(true);

    const ac = new AbortController();

    const tick = async () => {
      if (ac.signal.aborted) return;
      const since = bootstrappedRef.current ? nextSinceRef.current : 0;
      try {
        const data = await getConsumerCsMessages(
          auth.token!,
          conversationId,
          since,
          ac.signal,
        );
        nextSinceRef.current = data.nextSince;
        bootstrappedRef.current = true;
        setMessages((prev) => {
          if (since === 0) {
            return data.messages;
          }
          const map = new Map(prev.map((m) => [m.id, m]));
          for (const m of data.messages) {
            map.set(m.id, m);
          }
          return [...map.values()].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
        });
        setError(null);
      } catch (e) {
        if (!ac.signal.aborted) {
          setError((e as Error).message);
        }
      } finally {
        setLoading(false);
      }
    };

    void tick();
    const iv = setInterval(() => void tick(), 4000);
    const st = setInterval(() => void refreshTicketStatus(), 12_000);

    return () => {
      ac.abort();
      clearInterval(iv);
      clearInterval(st);
    };
  }, [auth.status, auth.token, conversationId, refreshTicketStatus]);

  async function onSend(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!auth.token || !conversationId || !draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      await sendConsumerCsMessage(auth.token, conversationId, draft.trim());
      setDraft('');
      bootstrappedRef.current = false;
      nextSinceRef.current = 0;
      const data = await getConsumerCsMessages(auth.token, conversationId, 0);
      nextSinceRef.current = data.nextSince;
      bootstrappedRef.current = true;
      setMessages(data.messages);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function onCsat(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!auth.token || !ticketId) return;
    setCsatBusy(true);
    setError(null);
    try {
      await submitConsumerCsCsat(auth.token, ticketId, {
        rating: csatRating,
        comment: csatComment.trim() || undefined,
      });
      setCsatDone(true);
      await refreshTicketStatus();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCsatBusy(false);
    }
  }

  const showCsat = Boolean(ticketId) && ticketStatus === 'RESOLVED' && !csatDone;

  return (
    <main className="min-h-screen pb-24">
      <header className="sticky top-0 z-20 flex items-center gap-3 bg-clay-50/85 px-5 pb-3 pt-5 backdrop-blur-md">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate('/me/cs')}
          className="grid h-9 w-9 place-items-center rounded-full border border-clay-200 bg-white p-0 text-clay-700"
          aria-label="back"
        >
          <BackGlyph />
        </Button>
        <span className="eyebrow flex-1">{tk('customerService.conversationTitle')}</span>
      </header>

      <div className="mt-2 space-y-4 px-5">
        {auth.status !== 'authenticated' ? (
          <div className="surface-card border-dashed p-8 text-center text-sm text-clay-500">
            {tk('me.unauth.body')}
          </div>
        ) : loading && messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-clay-400">{tk('common.loading')}</p>
        ) : (
          <>
            {error ? (
              <div className="rounded-2xl border border-danger-500/40 bg-danger-500/10 p-4 text-sm text-danger-500">
                {tk('common.error.generic')}
                <p className="mt-1 break-all text-xs opacity-80">{error}</p>
              </div>
            ) : null}

            <p className="text-xs text-clay-500">{tk('customerService.pollNote')}</p>

            <ul className="space-y-2 rounded-2xl border border-clay-100 bg-white p-3">
              {messages.length === 0 ? (
                <li className="py-6 text-center text-sm text-clay-500">{tk('customerService.empty')}</li>
              ) : (
                messages.map((m) => (
                  <li
                    key={m.id}
                    className={`rounded-xl px-3 py-2 text-sm ${
                      m.senderRole === 'CS_AGENT'
                        ? 'bg-amber-50 text-clay-900'
                        : m.senderRole === 'CONSUMER'
                          ? 'bg-clay-100 text-clay-900'
                          : 'bg-clay-50 text-clay-700'
                    }`}
                  >
                    <div className="text-[10px] font-medium uppercase text-clay-500">
                      {m.senderRole}
                    </div>
                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                    <div className="mt-1 text-[10px] text-clay-400">
                      {new Date(m.createdAt).toLocaleString()}
                    </div>
                  </li>
                ))
              )}
            </ul>

            <form onSubmit={(ev) => void onSend(ev)} className="surface-card space-y-2 p-4">
              <label className="block text-xs text-clay-600">
                {tk('customerService.yourMessage')}
                <textarea
                  value={draft}
                  onChange={(ev) => setDraft(ev.target.value)}
                  required
                  maxLength={5000}
                  rows={3}
                  className="mt-1 w-full rounded-xl border border-clay-200 bg-white px-3 py-2 text-sm"
                />
              </label>
              <Button type="submit" disabled={sending || !draft.trim()} className="w-full">
                {sending ? tk('common.loading') : tk('customerService.send')}
              </Button>
            </form>

            {ticketId ? (
              <p className="text-xs text-clay-500">
                {tk('customerService.ticketStatusLabel')}: {ticketStatus ?? '—'}
              </p>
            ) : null}

            {showCsat ? (
              <form onSubmit={(ev) => void onCsat(ev)} className="surface-card space-y-3 p-4">
                <h2 className="text-sm font-semibold text-clay-900">{tk('customerService.csatTitle')}</h2>
                <p className="text-xs text-clay-600">{tk('customerService.csatHint')}</p>
                <label className="block text-xs text-clay-600">
                  {tk('customerService.csatStars')}
                  <input
                    type="range"
                    min={1}
                    max={5}
                    step={1}
                    value={csatRating}
                    onChange={(ev) => setCsatRating(Number(ev.target.value))}
                    className="mt-2 w-full"
                  />
                  <span className="mt-1 block text-sm font-semibold">{csatRating}</span>
                </label>
                <label className="block text-xs text-clay-600">
                  {tk('customerService.csatComment')}
                  <textarea
                    value={csatComment}
                    onChange={(ev) => setCsatComment(ev.target.value)}
                    maxLength={2000}
                    rows={2}
                    className="mt-1 w-full rounded-xl border border-clay-200 bg-white px-3 py-2 text-sm"
                  />
                </label>
                <Button type="submit" disabled={csatBusy} className="w-full">
                  {csatBusy ? tk('common.loading') : tk('customerService.csatSubmit')}
                </Button>
              </form>
            ) : null}

            {csatDone ? (
              <p className="text-center text-sm text-emerald-700">{tk('customerService.csatThanks')}</p>
            ) : null}
          </>
        )}
      </div>

      <BottomNav />
    </main>
  );
}

function BackGlyph(): React.ReactElement {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
